import { useState, useEffect } from 'react';
import { FormControl } from '@mui/material';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import FieldNames from '../../../defaults/FieldNames.json';
import { useSelector } from 'react-redux';
import BasicNumberField from './BasicNumberField';
import Events from '../../../defaults/Events';
import { isFeaturePostFire } from '../../../utils/FeatureHelper';

const useStyles = makeStyles({
    underline: {
        '&&&:before': {
            borderBottom: '2px solid red',
        },
        '&&:after': {
            borderBottom: '2px solid red',
        },
    },
    MuiFormHelperText: {
        root: {
            height: '100%',
            display: 'block',
        },
    },
});

const useNotSavedStyles = makeStyles({
    underline: {
        '&&&:before': {
            borderBottom: '2px solid gold',
        },
        '&&:after': {
            borderBottom: '2px solid gold',
        },
    },
    MuiFormHelperText: {
        root: {
            height: '100%',
            display: 'block',
        },
    },
});

const theme = createTheme({
    overrides: {},
});

export default function DetailsNumber({
    detail,
    field,
    onFieldChange,
    isRequired,
    fieldIsStatic,
    getAllApprovalFieldsCount,
    max,
    min,
}) {
    const treatmentValidationUnsavedValues = useSelector((state) => {
        return state.treatmentValidation;
    });
    const activityValidationUnsavedValues = useSelector((state) => {
        return state.activityValidation;
    });
    const actualsTreatmentValidationUnsavedValues = useSelector((state) => {
        return state.actualsTreatmentValidation;
    });
    const actualsActivityValidationUnsavedValues = useSelector((state) => {
        return state.actualsActivityValidation;
    });

    const classes = useStyles();
    const notSavedClasses = useNotSavedStyles();

    const [value, setValue] = useState('');
    const [helperText, setHelperText] = useState('');
    const [error, setError] = useState(false);
    const [isFieldDisabled, setIsFieldDisabled] = useState(false);

    useEffect(() => {
        if (detail && detail.attributes) {
            const isPostFire = isFeaturePostFire(detail.attributes);
            if (detail.attributes[field] === null || detail.attributes[field] === undefined) {
                if (
                    field === FieldNames.featureFields.measureAmount &&
                    isPostFire &&
                    (detail.attributes[FieldNames.featureFields.joinFeature] ===
                        FieldNames.joinFeatureValues.line ||
                        detail.attributes[FieldNames.featureFields.joinFeature] ===
                            FieldNames.joinFeatureValues.polygon)
                ) {
                    setValue(detail.attributes[FieldNames.featureFields.calculatedArea]);
                    onFieldChange(
                        FieldNames.featureFields.measureAmount,
                        detail.attributes[FieldNames.featureFields.calculatedArea]
                    );
                    setHelperText('');
                    setError(false);
                    setIsFieldDisabled(true);
                } else {
                    setValue('');
                    setHelperText('');
                    setError(false);
                    setIsFieldDisabled(false);
                }
            } else if (
                field === FieldNames.featureFields.vegDeparturePercentageManual &&
                parseInt(detail.attributes[field]) > 100
            ) {
                setHelperText(
                    'The Vegetation Departure Percentage Derived at more than 100. Please manually enter a number from 0 to 100'
                );
                setValue('');
                setError(true);
            } else if (
                field === FieldNames.featureFields.vegDeparturePercentageManual &&
                parseInt(detail.attributes[field]) < 0
            ) {
                setHelperText(
                    'The Vegetation Departure Percentage Derived at less than 100. Please manually enter a number from 0 to 100'
                );
                setValue('');
                setError(true);
            } else {
                if (
                    field === FieldNames.featureFields.measureAmount &&
                    isPostFire &&
                    (detail.attributes[FieldNames.featureFields.joinFeature] ===
                        FieldNames.joinFeatureValues.line ||
                        detail.attributes[FieldNames.featureFields.joinFeature] ===
                            FieldNames.joinFeatureValues.polygon)
                ) {
                    setIsFieldDisabled(true);
                } else {
                    setIsFieldDisabled(false);
                }
                setValue(detail.attributes[field]);
                setHelperText('');
                setError(false);
            }
        }
    }, [detail, field]);

    const inputValueMissing = () => {
        if (detail && detail.attributes) {
            if (!fieldIsStatic && !detail.attributes[field]) {
                return true;
            } else {
                return false;
            }
        } else {
            return true;
        }
    };

    const showNotSavedClasses = (field) => {
        var fieldState = false;
        if (
            detail &&
            detail.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.treatment
        ) {
            // Estimated Treatment
            if (treatmentValidationUnsavedValues === null || !field || field.length === 0) {
                return false;
            }
            fieldState = treatmentValidationUnsavedValues[field];
            return fieldState;
        } else if (
            detail &&
            detail.attributes[FieldNames.featureFields.class] ===
                FieldNames.classTypes.actualTreatment
        ) {
            // Actual Treatment
            if (actualsTreatmentValidationUnsavedValues === null || !field || field.length === 0) {
                return false;
            }
            fieldState = actualsTreatmentValidationUnsavedValues[field];
            return fieldState;
        } else if (
            detail &&
            detail.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.activity
        ) {
            // Estimated Activity
            if (activityValidationUnsavedValues === null || !field || field.length === 0) {
                return false;
            }
            fieldState = activityValidationUnsavedValues[field];
            return fieldState;
        } else if (
            detail &&
            detail.attributes[FieldNames.featureFields.class] ===
                FieldNames.classTypes.actualActivity
        ) {
            // Actual Activity
            if (actualsActivityValidationUnsavedValues === null || !field || field.length === 0) {
                return false;
            }
            fieldState = actualsActivityValidationUnsavedValues[field];
            return fieldState;
        } else {
            return false;
        }
    };

    return (
        <FormControl variant='standard' fullWidth>
            <ThemeProvider theme={theme}>
                <BasicNumberField
                    detail={detail}
                    onFieldChange={onFieldChange}
                    field={field}
                    label=''
                    value={value}
                    setValue={setValue}
                    variant={'standard'}
                    placeholder={''}
                    useFullWidth={true}
                    helperText={helperText ? helperText : ''}
                    isError={error}
                    useMultiline={true}
                    isDisabled={
                        fieldIsStatic ||
                        getAllApprovalFieldsCount(detail, isRequired) ||
                        isFieldDisabled
                    }
                    inputProps={{
                        ...(isRequired && inputValueMissing()
                            ? { InputProps: { classes } }
                            : showNotSavedClasses(field)
                              ? { InputProps: { classes: notSavedClasses } }
                              : { InputProps: {} }),
                    }}
                    triggerEvent={Events.TriggerFieldChange}
                />
            </ThemeProvider>
        </FormControl>
    );
}

DetailsNumber.propTypes = {
    detail: PropTypes.any,
    onFieldChange: PropTypes.func.isRequired,
    field: PropTypes.string.isRequired,
    isRequired: PropTypes.bool.isRequired,
    fieldIsStatic: PropTypes.bool.isRequired,
    getAllApprovalFieldsCount: PropTypes.func.isRequired,
    max: PropTypes.number,
    min: PropTypes.number,
};
